// features/workstations/dashboard-sections/decision-journal.js
// ═══════════════════════════════════════════════════════════
// DECISION JOURNAL — Tier B learning
// ═══════════════════════════════════════════════════════════
// Every consequential decision — including the PASSES and
// SKIPS. Captured contemporaneously with reasoning + expected
// outcome + confidence. Resolved later with actual outcome.
//
// Why passes matter: “I passed on TSLA because earnings
// tomorrow” is as important a decision as taking the trade.
// The journal keeps it in the record.
//
// Pairs with Mistake Log (retrospective) and Strategy Health
// (aggregate grade). This is the living record that fills both.
// ═══════════════════════════════════════════════════════════

import { useCallback, useEffect, useState } from 'react';
import {
  Eyebrow,
  SerifHeadline,
  SerifSummary,
  SectionStrip,
  PillRow,
  PillTable,
  MetaField,
  PractitionerLens,
} from '../../../components/widgets/primitives';

const KIND_OPTIONS = [
  { key: 'entered',  label: 'Entered' },
  { key: 'passed',   label: 'Passed'  },
  { key: 'exited',   label: 'Exited'  },
  { key: 'sized',    label: 'Sized'   },
  { key: 'pivoted',  label: 'Pivoted' },
  { key: 'held',     label: 'Held'    },
  { key: 'other',    label: 'Other'   },
];

const KIND_TONES = {
  entered: 'sage',
  passed:  'steel',
  exited:  'gold',
  sized:   'amber',
  pivoted: 'purple',
  held:    'steel',
  other:   'steel',
};

const OUTCOME_OPTIONS = [
  { key: 'right',         label: 'Right' },
  { key: 'wrong',         label: 'Wrong' },
  { key: 'partial',       label: 'Partial' },
  { key: 'inconclusive',  label: 'Inconclusive' },
];

const OUTCOME_TONES = {
  right:         'sage',
  wrong:         'terra',
  partial:       'amber',
  inconclusive:  'steel',
};

// ═══════════════════════════════════════════════════════════
// SECTION
// ═══════════════════════════════════════════════════════════
export default function DecisionJournalSection({ workstation, userId }) {
  const [data, setData] = useState({ decisions: [], tally: null });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [composing, setComposing] = useState(false);
  const [resolvingId, setResolvingId] = useState(null);
  const [filterKind, setFilterKind] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all'); // all | open | resolved
  const [windowDays, setWindowDays] = useState(30);

  const load = useCallback(async () => {
    if (!workstation?.id || !userId) return;
    setLoading(true);
    setError(null);
    try {
      const params = {
        workstation_id: workstation.id,
        user_id: userId,
        days: String(windowDays),
        status: filterStatus,
      };
      if (filterKind !== 'all') params.kind = filterKind;
      const qs = new URLSearchParams(params);
      const res = await fetch(`/api/workstations/decisions?${qs}`, { cache: 'no-store' });
      const payload = res.ok ? await res.json() : null;
      if (!res.ok) setError(payload?.error || `HTTP ${res.status}`);
      setData({
        decisions: Array.isArray(payload?.decisions) ? payload.decisions : [],
        tally: payload?.tally || null,
      });
    } catch (e) {
      setError(e?.message || 'Load failed');
    } finally {
      setLoading(false);
    }
  }, [workstation?.id, userId, filterKind, filterStatus, windowDays]);

  useEffect(() => { load(); }, [load]);

  const create = useCallback(async (payload) => {
    setSaving(true);
    setError(null);
    try {
      const res = await fetch('/api/workstations/decisions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          workstation_id: workstation.id,
          user_id: userId,
          ...payload,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data?.error || `HTTP ${res.status}`);
        return false;
      }
      setComposing(false);
      await load();
      return true;
    } catch (e) {
      setError(e?.message || 'Save failed');
      return false;
    } finally {
      setSaving(false);
    }
  }, [workstation?.id, userId, load]);

  const resolve = useCallback(async (id, payload) => {
    setSaving(true);
    setError(null);
    try {
      const res = await fetch('/api/workstations/decisions', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, user_id: userId, ...payload }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data?.error || `HTTP ${res.status}`);
        return false;
      }
      setResolvingId(null);
      await load();
      return true;
    } catch (e) {
      setError(e?.message || 'Resolve failed');
      return false;
    } finally {
      setSaving(false);
    }
  }, [userId, load]);

  const remove = useCallback(async (id) => {
    if (!confirm('Delete this decision? This cannot be undone.')) return;
    setSaving(true);
    try {
      const qs = new URLSearchParams({ id, user_id: userId });
      const res = await fetch(`/api/workstations/decisions?${qs}`, { method: 'DELETE' });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        setError(j?.error || `HTTP ${res.status}`);
        return;
      }
      await load();
    } catch (e) {
      setError(e?.message || 'Delete failed');
    } finally {
      setSaving(false);
    }
  }, [userId, load]);

  return (
    <section className="ws-decisions">
      {loading && <div className="ws-decisions-loading">Loading…</div>}

      {!loading && (
        <DecisionsBody
          data={data}
          saving={saving}
          error={error}
          composing={composing}
          setComposing={setComposing}
          resolvingId={resolvingId}
          setResolvingId={setResolvingId}
          filterKind={filterKind}
          setFilterKind={setFilterKind}
          filterStatus={filterStatus}
          setFilterStatus={setFilterStatus}
          windowDays={windowDays}
          setWindowDays={setWindowDays}
          onCreate={create}
          onResolve={resolve}
          onDelete={remove}
        />
      )}
    </section>
  );
}

// ═══════════════════════════════════════════════════════════
// BODY
// ═══════════════════════════════════════════════════════════
function DecisionsBody({
  data, saving, error,
  composing, setComposing,
  resolvingId, setResolvingId,
  filterKind, setFilterKind,
  filterStatus, setFilterStatus,
  windowDays, setWindowDays,
  onCreate, onResolve, onDelete,
}) {
  const tally = data.tally;
  const decisions = Array.isArray(data.decisions) ? data.decisions : [];
  const total = tally?.total_count || 0;
  const open = tally?.open_count || 0;
  const resolved = tally?.resolved_count || 0;
  const accuracy = tally?.accuracy_rate;

  const headline = total === 0
    ? 'No decisions logged in window.'
    : `${total} decision${total === 1 ? '' : 's'} · ${resolved} resolved · ${open} open`;

  const summaryParts = [];
  if (accuracy != null) summaryParts.push(`${Math.round(accuracy * 100)}% right on resolved`);
  if (tally?.outcomes?.wrong > 0) summaryParts.push(`${tally.outcomes.wrong} wrong`);
  const summary = total === 0
    ? 'Log what you decided AND why — including passes. Resolve later when the outcome is known. Builds the evidence log for your edge.'
    : summaryParts.length
      ? summaryParts.join(' · ') + '.'
      : 'Log what you decided AND why — including passes.';

  return (
    <>
      <Eyebrow parts={[
        'decision journal',
        `${windowDays}d window`,
        total > 0 ? `${total} logged` : 'empty',
      ]} />

      <SerifHeadline>
        <span className={accuracy != null && accuracy >= 0.6 ? 'ws-decisions-tone-sage'
          : accuracy != null && accuracy < 0.5 ? 'ws-decisions-tone-terra'
          : 'ws-decisions-tone-steel'}>
          {headline}
        </span>
      </SerifHeadline>
      <SerifSummary>{summary}</SerifSummary>

      {error && <div className="ws-decisions-error">{error}</div>}

      <div className="ws-decisions-actions">
        <button
          type="button"
          className="ws-decisions-btn ws-decisions-btn-sage"
          onClick={() => setComposing(!composing)}
          disabled={saving}
        >
          {composing ? '✕ cancel' : '+ log decision'}
        </button>

        <div className="ws-decisions-window-chips">
          {[7, 14, 30, 60, 90].map((d) => (
            <button
              key={d}
              type="button"
              className={`ws-decisions-window-chip ${d === windowDays ? 'is-active' : ''}`}
              onClick={() => setWindowDays(d)}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {composing && (
        <ComposeDecision
          saving={saving}
          onSubmit={onCreate}
          onCancel={() => setComposing(false)}
        />
      )}

      {/* Outcome tally */}
      {tally && resolved > 0 && (
        <SectionStrip label="outcomes (resolved)" count={resolved}>
          <PillTable>
            {OUTCOME_OPTIONS.map((o) => (
              tally.outcomes[o.key] > 0 && (
                <PillRow
                  key={o.key}
                  keyText={o.label}
                  valueText={String(tally.outcomes[o.key])}
                  tone={OUTCOME_TONES[o.key]}
                />
              )
            ))}
          </PillTable>
        </SectionStrip>
      )}

      {/* Status filter */}
      <div className="ws-decisions-filter-row">
        <span className="ws-decisions-filter-label">status</span>
        {['all', 'open', 'resolved'].map((s) => (
          <button
            key={s}
            type="button"
            className={`ws-decisions-filter-chip ${s === filterStatus ? 'is-active' : ''}`}
            onClick={() => setFilterStatus(s)}
          >
            {s}
          </button>
        ))}
      </div>

      {/* Kind filter */}
      <div className="ws-decisions-filter-row">
        <span className="ws-decisions-filter-label">kind</span>
        <button
          type="button"
          className={`ws-decisions-filter-chip ${filterKind === 'all' ? 'is-active' : ''}`}
          onClick={() => setFilterKind('all')}
        >
          all
        </button>
        {KIND_OPTIONS.map((k) => (
          <button
            key={k.key}
            type="button"
            className={`ws-decisions-filter-chip ${filterKind === k.key ? 'is-active' : ''}`}
            onClick={() => setFilterKind(k.key)}
          >
            {k.label.toLowerCase()}
          </button>
        ))}
      </div>

      {/* Decision cards */}
      {decisions.length > 0 && (
        <SectionStrip
          label={`recent — ${filterStatus === 'all' ? 'all' : filterStatus}${filterKind !== 'all' ? ` · ${filterKind}` : ''}`}
          count={decisions.length}
        >
          <div className="ws-decisions-list">
            {decisions.map((d) => (
              <DecisionCard
                key={d.id}
                d={d}
                saving={saving}
                isResolving={resolvingId === d.id}
                onResolveOpen={() => setResolvingId(d.id)}
                onResolveCancel={() => setResolvingId(null)}
                onResolve={(payload) => onResolve(d.id, payload)}
                onDelete={() => onDelete(d.id)}
              />
            ))}
          </div>
        </SectionStrip>
      )}

      <PractitionerLens label="why the journal matters">
        Every decision with reasoning + expected outcome is a future
        calibration point. When you log a pass and six weeks later
        you can see ‘I was right to skip this one, here’s why,’
        that’s edge learning you can’t get from P&L alone.
      </PractitionerLens>
    </>
  );
}

// ═══════════════════════════════════════════════════════════
// DECISION CARD
// ═══════════════════════════════════════════════════════════
function DecisionCard({ d, saving, isResolving, onResolveOpen, onResolveCancel, onResolve, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const kindLabel = KIND_OPTIONS.find(k => k.key === d.kind)?.label || d.kind;
  const tone = KIND_TONES[d.kind] || 'steel';
  const isOpen = !d.actual_outcome;
  const outcomeTone = d.outcome_kind ? OUTCOME_TONES[d.outcome_kind] : null;

  return (
    <div className={`ws-decisions-card ws-decisions-card-${tone} ${isOpen ? 'is-open' : 'is-resolved'}`}>
      <button
        type="button"
        className="ws-decisions-card-head"
        onClick={() => setExpanded((v) => !v)}
      >
        <div className="ws-decisions-card-row">
          <span className={`ws-decisions-kind ws-decisions-kind-${tone}`}>{kindLabel}</span>
          {d.instrument && <span className="ws-decisions-instrument">{d.instrument}</span>}
          {d.confidence && (
            <span className="ws-decisions-confidence" title="Confidence at time of decision (1–5)">
              {'◆'.repeat(d.confidence)}{'◇'.repeat(5 - d.confidence)}
            </span>
          )}
          {d.outcome_kind && (
            <span className={`ws-decisions-outcome ws-decisions-outcome-${outcomeTone}`}>
              {d.outcome_kind}
            </span>
          )}
          <span className="ws-decisions-when">{fmtWhen(d.occurred_at)}</span>
        </div>
        <div className="ws-decisions-decision">{d.decision}</div>
      </button>

      {expanded && (
        <div className="ws-decisions-drawer">
          {d.reasoning && <MetaField label="reasoning">{d.reasoning}</MetaField>}
          {d.expected_outcome && <MetaField label="expected">{d.expected_outcome}</MetaField>}
          {d.actual_outcome && <MetaField label="actual">{d.actual_outcome}</MetaField>}
          {d.outcome_notes && <MetaField label="outcome notes">{d.outcome_notes}</MetaField>}

          <div className="ws-decisions-drawer-actions">
            {isOpen && !isResolving && (
              <button
                type="button"
                className="ws-decisions-btn ws-decisions-btn-sage"
                onClick={onResolveOpen}
                disabled={saving}
              >
                resolve…
              </button>
            )}
            <button
              type="button"
              className="ws-decisions-btn ws-decisions-btn-terra"
              onClick={onDelete}
              disabled={saving}
            >
              delete
            </button>
          </div>

          {isResolving && (
            <ResolveForm
              saving={saving}
              onSubmit={onResolve}
              onCancel={onResolveCancel}
            />
          )}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// COMPOSE FORM
// ═══════════════════════════════════════════════════════════
function ComposeDecision({ saving, onSubmit, onCancel }) {
  const [kind, setKind] = useState('entered');
  const [instrument, setInstrument] = useState('');
  const [decision, setDecision] = useState('');
  const [reasoning, setReasoning] = useState('');
  const [expected, setExpected] = useState('');
  const [confidence, setConfidence] = useState('');

  const canSubmit = decision.trim().length >= 3 && !saving;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    const ok = await onSubmit({
      kind,
      instrument: instrument.trim() || undefined,
      decision: decision.trim(),
      reasoning: reasoning.trim() || undefined,
      expected_outcome: expected.trim() || undefined,
      confidence: confidence === '' ? undefined : Number(confidence),
    });
    if (ok) {
      setDecision(''); setReasoning(''); setExpected('');
      setInstrument(''); setConfidence('');
      setKind('entered');
    }
  };

  return (
    <form className="ws-decisions-compose" onSubmit={submit}>
      <div className="ws-decisions-field-row">
        <label className="ws-decisions-field-inline">
          <span className="ws-decisions-field-label">kind</span>
          <select
            className="ws-decisions-select"
            value={kind}
            onChange={(e) => setKind(e.target.value)}
          >
            {KIND_OPTIONS.map((k) => (
              <option key={k.key} value={k.key}>{k.label}</option>
            ))}
          </select>
        </label>
        <label className="ws-decisions-field-inline">
          <span className="ws-decisions-field-label">instrument</span>
          <input
            type="text"
            maxLength={32}
            className="ws-decisions-input ws-decisions-input-instrument"
            value={instrument}
            onChange={(e) => setInstrument(e.target.value.toUpperCase())}
            placeholder="optional"
          />
        </label>
        <label className="ws-decisions-field-inline">
          <span className="ws-decisions-field-label">confidence</span>
          <select
            className="ws-decisions-select ws-decisions-select-small"
            value={confidence}
            onChange={(e) => setConfidence(e.target.value)}
          >
            <option value="">—</option>
            <option value="1">1 · guess</option>
            <option value="2">2 · lean</option>
            <option value="3">3 · balanced</option>
            <option value="4">4 · strong</option>
            <option value="5">5 · high</option>
          </select>
        </label>
      </div>

      <label className="ws-decisions-field">
        <span className="ws-decisions-field-label">decision</span>
        <textarea
          className="ws-decisions-textarea"
          rows={2}
          value={decision}
          onChange={(e) => setDecision(e.target.value)}
          placeholder="What did you decide? ‘Passed on TSLA pre-earnings’ / ‘Entered QQQ call debit 5-min reversal’."
          autoFocus
        />
      </label>

      <label className="ws-decisions-field">
        <span className="ws-decisions-field-label">reasoning</span>
        <textarea
          className="ws-decisions-textarea"
          rows={2}
          value={reasoning}
          onChange={(e) => setReasoning(e.target.value)}
          placeholder="Why. The more specific, the better."
        />
      </label>

      <label className="ws-decisions-field">
        <span className="ws-decisions-field-label">expected outcome</span>
        <textarea
          className="ws-decisions-textarea"
          rows={2}
          value={expected}
          onChange={(e) => setExpected(e.target.value)}
          placeholder="What you think will happen — the testable prediction."
        />
      </label>

      <div className="ws-decisions-actions">
        <button type="submit" disabled={!canSubmit} className="ws-decisions-btn ws-decisions-btn-sage">
          {saving ? 'saving…' : 'log'}
        </button>
        <button type="button" onClick={onCancel} disabled={saving} className="ws-decisions-btn ws-decisions-btn-steel">
          cancel
        </button>
      </div>
    </form>
  );
}

// ═══════════════════════════════════════════════════════════
// RESOLVE FORM
// ═══════════════════════════════════════════════════════════
function ResolveForm({ saving, onSubmit, onCancel }) {
  const [actualOutcome, setActualOutcome] = useState('');
  const [outcomeKind, setOutcomeKind] = useState('right');
  const [notes, setNotes] = useState('');

  const canSubmit = actualOutcome.trim().length >= 3 && !saving;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    await onSubmit({
      actual_outcome: actualOutcome.trim(),
      outcome_kind: outcomeKind,
      outcome_notes: notes.trim() || undefined,
    });
  };

  return (
    <form className="ws-decisions-resolve" onSubmit={submit}>
      <div className="ws-decisions-field-row">
        <label className="ws-decisions-field-inline">
          <span className="ws-decisions-field-label">verdict</span>
          <select
            className="ws-decisions-select"
            value={outcomeKind}
            onChange={(e) => setOutcomeKind(e.target.value)}
          >
            {OUTCOME_OPTIONS.map((o) => (
              <option key={o.key} value={o.key}>{o.label}</option>
            ))}
          </select>
        </label>
      </div>
      <label className="ws-decisions-field">
        <span className="ws-decisions-field-label">what actually happened</span>
        <textarea
          className="ws-decisions-textarea"
          rows={2}
          value={actualOutcome}
          onChange={(e) => setActualOutcome(e.target.value)}
          placeholder="The outcome — specific."
          autoFocus
        />
      </label>
      <label className="ws-decisions-field">
        <span className="ws-decisions-field-label">notes (optional)</span>
        <textarea
          className="ws-decisions-textarea"
          rows={1}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Extra context — what you learned, whether the reasoning held up."
        />
      </label>
      <div className="ws-decisions-actions">
        <button type="submit" disabled={!canSubmit} className="ws-decisions-btn ws-decisions-btn-sage">
          {saving ? 'resolving…' : 'resolve'}
        </button>
        <button type="button" onClick={onCancel} disabled={saving} className="ws-decisions-btn ws-decisions-btn-steel">
          cancel
        </button>
      </div>
    </form>
  );
}

// ─── Helpers ───
function fmtWhen(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const diffMs = Date.now() - d.getTime();
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffDays === 0) {
      const diffH = Math.floor(diffMs / 3600000);
      if (diffH === 0) return 'just now';
      return `${diffH}h ago`;
    }
    if (diffDays === 1) return 'yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}
